import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AppInput from "../../components/Core/AppInput/AppInput";
import Flex from "../../components/Layout/Flex";
import { DocumentLinkBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { routes } from "../../configs/constants/routes";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import Button from "../../components/Core/Button/Button";
import { FormLabel } from "../../components/Core/AppText/AppText";
import Checkbox from "../../components/Core/Checkbox/Checkbox";
import { useCompetence, useUploadDocuments } from "../../networking/organisation.hooks";
import { Alert, AlertDescription, useToast } from "@chakra-ui/react";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import { validateAndFormatURL } from "../../utils/string.utils";

const DocumentLinkPage: React.FC = () => {
	const { t } = useTranslation();
	const toast = useToast();
	const navigate = useNavigate();
	const { data: competenceList, isLoading: competenceLoading } = useCompetence(true);
	const [selectedBranches, setSelectedBranches] = useState<Array<string>>([]);
	const [allBranches, setAllBranches] = useState<boolean>(false);
	const [title, setTitle] = useState<string>("");
	const [link, setLink] = useState<string>("");

	const {
		mutate: publishDoc,
		isLoading: publishing,
		isSuccess: documentPublished,
		isError: documentPublishError,
	} = useUploadDocuments();

	function publishDocument() {
		if ((selectedBranches.length > 0 || allBranches) && link && title) {
			const params: {
				branches: Array<string>;
				mimeType: string;
				title: string;
				type: string;
				url: string;
			} = {
				branches: allBranches ? [] : selectedBranches,
				mimeType: "",
				title: title,
				type: "link",
				url: validateAndFormatURL(link),
			};
			publishDoc(params);
		} else {
			toast({
				id: "documentPublish validation",
				render: () => (
					<Alert status="warning" borderWidth={2} borderRadius={4} variant={"solid"}>
						<AlertDescription>{t("DOCUMENT_FILL_IN_MESSAGE")}</AlertDescription>
					</Alert>
				),
				isClosable: true,
				position: "bottom-right",
				duration: 2 * 1000,
			});
		}
	}

	useEffect(() => {
		if (documentPublished) {
			toast({
				id: "documentPublished",
				render: () => (
					<Alert status="success" borderWidth={2} borderRadius={4} variant={"solid"}>
						<AlertDescription>{t("DOCUMENT_PUBLISHED_MESSAGE")}</AlertDescription>
					</Alert>
				),
				isClosable: true,
				position: "top",
				duration: 3 * 1000,
				onCloseComplete: () => handleRefresh(),
			});
		}
		if (documentPublishError) {
			toast({
				id: "documentPublished error",
				render: () => (
					<Alert status="error" borderWidth={2} borderRadius={4} variant={"solid"}>
						<AlertDescription>{t("DOCUMENT_PUBLISH_ERROR")}</AlertDescription>
					</Alert>
				),
				isClosable: true,
				position: "top",
				duration: 3 * 1000,
			});
		}
	}, [documentPublished, documentPublishError]);

	const handleRefresh = () => {
		navigate(routes.DOCUMENTS);
	};

	return (
		<div className="p-4 px-6">
			<Breadcrumbs items={DocumentLinkBreadcrumbs} />
			<Flex direction="column" width="50%">
				<Flex margin="0 0 10px 0">
					<AppInput
						label={t("LINK")}
						margin="0.5rem 0"
						placeholder={"URL_PLACEHOLDER"}
						value={link}
						onChange={(e: any) => setLink(e.target.value)}
					/>
				</Flex>

				<Flex direction="column">
					<AppInput
						label={t("TITLE")}
						margin="0.5rem 0"
						placeholder={t("TITLE")}
						value={title}
						onChange={(e: any) => setTitle(e.target.value)}
					/>
				</Flex>
				<Flex direction="column" margin="0 1rem 0 0">
					<FormLabel>{t("showConsultants")}</FormLabel>
					<Checkbox
						margin="0.5rem 0"
						label="Alla branscher"
						isChecked={allBranches}
						onChange={(e: any) => {
							setAllBranches(e.target.checked);
							if (e.target.checked) {
								setSelectedBranches([]);
							}
						}}
					/>
				</Flex>
				<Flex direction="column" margin="0 1rem 0 1rem">
					{competenceLoading ? (
						<LoadingIndicator />
					) : (
						competenceList?.competences &&
						Array.isArray(competenceList.competences) &&
						competenceList.competences.map((compt) => {
							return (
								<Checkbox
									margin="0.5rem 0"
									label={compt.name.sv}
									value={compt.key}
									isChecked={selectedBranches.includes(compt.key)}
									disabled={allBranches}
									onChange={(e: any) => {
										e.target.checked
											? setSelectedBranches([...selectedBranches, compt.key])
											: setSelectedBranches(
													selectedBranches.filter((branch) => branch !== compt.key)
											  );
									}}
								/>
							);
						})
					)}
				</Flex>
			</Flex>
			<Flex direction="row" width="100%" justifyContent="center" margin="2rem 10rem 0 0rem">
				<Button width="auto" loading={publishing} onClick={() => publishDocument()}>
					{t("publishDocuments")}
				</Button>
			</Flex>
		</div>
	);
};

export default DocumentLinkPage;
